import type { CompositionV2Entity } from '@revelio/data-access';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { ChartFallback } from '../../Chart/ChartFallback';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
} from '../types';
import { convertTotalSpendData } from '../utils/convert-total-spend-data';
import { usePayAndCompositionQueries } from './usePayAndCompositionQueries';

type TotalSpendDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: 'geography' | 'role';
  metric: 'total_spend_salary' | 'total_spend_compensation';
  chartType: 'stacked-bar' | 'grouped-bar' | 'table' | 'treemap';
  exchangeRate?: number;
  entitiesOverride?: CompositionV2Entity[];
  children: DataProviderChildren<DataProviderSet | undefined>;
};

export const TotalSpendDataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  chartType: _chartType,
  exchangeRate = 1,
  entitiesOverride,
  children,
}: TotalSpendDataProviderProps) => {
  const { payData, compositionData, status } = usePayAndCompositionQueries({
    primaryDimension,
    secondaryDimension,
    entitiesOverride,
  });

  const data =
    payData && compositionData
      ? convertTotalSpendData({
          payData,
          compositionData,
          metric,
          secondaryDimension,
          exchangeRate,
        })
      : undefined;

  return (
    <ChartFallback status={status}>
      {children({
        data,
        status:
          status === DataProviderQueryStatus.Success && !data
            ? DataProviderQueryStatus.Idle
            : status,
      })}
    </ChartFallback>
  );
};
